// Simulate mode — a deterministic, seeded generator that produces the SAME
// telemetry shape the real pipeline will (see telemetryState.js) over the
// REAL roster (actual instances/hosts), so the stage, HUD and anomaly engine
// run unchanged. Nothing here writes anywhere; the stage shows a SIMULATED
// watermark while this is the source.
//
// Each slug has a personality (base req/min, token rate, error floor); a
// diurnal curve + seeded noise shapes the day; scenarios inject a fault in
// the last N minutes relative to the virtual clock so you can watch it open.
export const SCENARIOS = [
  { id: 'calm', label: 'Calm day', text: 'Normal diurnal traffic, no faults.' },
  { id: 'spike', label: 'Traffic spike', text: 'Requests on the focus app jump 8× for the last 12 minutes.' },
  { id: 's5xx', label: '5xx burst', text: '6% of the focus app\'s requests fail for the last 8 minutes.' },
  { id: 'latency', label: 'Latency degradation', text: 'p95 climbs from ~250 ms to ~2 s over the last 15 minutes.' },
  { id: 'cost', label: 'Cost runaway', text: 'Token usage on the focus app runs 10× for the last 30 minutes.' },
  { id: 'queue', label: 'Stuck worker queue', text: 'Jobs queue up on the focus app while its worker stops heart-beating.' },
  { id: 'idle', label: 'Idle app', text: 'The focus app goes silent 26 hours ago after a busy week (7d window shows it).' },
  { id: 'cascade', label: 'Cross-app cascade', text: 'An upstream app fails; a flow-linked app degrades two minutes later.' },
  // Stage-state scenarios: nothing in the traffic rows changes; the
  // topology stage overlays the state (commandState.deriveTopology).
  { id: 'service-down', label: 'Service down', text: 'The focus app\'s first container exits 137; the next one restarts. 5xx follow for the last 6 minutes.' },
  { id: 'deploy-running', label: 'Deploy running', text: 'A UAT deploy is in progress on the focus app (sweep + deploying bead).' },
  { id: 'tls-expiring', label: 'TLS expiring', text: 'The focus app\'s primary certificate has 5 days left.' },
  { id: 'scan-running', label: 'Security scan running', text: 'An assurance scan is sweeping the focus app\'s shield.' },
];

// mulberry32 — tiny seeded PRNG; hash a string to a seed.
const hash = (s) => { let h = 2166136261; for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };
const rng = (seed) => { let a = seed >>> 0; return () => { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; };
// Smooth per-minute noise: value noise over minute index.
const noiseAt = (seed, minute) => { const i = Math.floor(minute); const f = minute - i; const a = rng(seed ^ hash(String(i)))(); const b = rng(seed ^ hash(String(i + 1)))(); const u = f * f * (3 - 2 * f); return a * (1 - u) + b * u; };
// Diurnal multiplier: quiet 02:00–06:00 UTC-7, peaks late morning + evening.
const diurnal = (ts) => { const h = ((ts / 3600e3) - 7 + 24 * 1000) % 24; return 0.25 + 0.75 * (0.5 + 0.5 * Math.sin(((h - 10) / 24) * Math.PI * 2)) + 0.25 * Math.exp(-((h - 20) ** 2) / 6); };

function personality(n, seed) {
  const r = rng(seed ^ hash(n.slug));
  const kind = n.pool ? 'pool' : n.envRole === 'uat' ? 'uat' : n.external ? 'external' : 'managed';
  const base = kind === 'pool' ? 0 : kind === 'uat' ? 2 + r() * 4 : kind === 'external' ? 8 + r() * 40 : 15 + r() * 90;
  return { kind, base, errFloor: 0.001 + r() * 0.004, p95: 160 + r() * 220, tokensPerReq: 40 + r() * 400, convoEvery: 8 + Math.round(r() * 30), bytesPerReq: 6e3 + r() * 40e3 };
}

// Scenario envelope for (slug, t): multiplicative/additive adjustments.
function fault(scn, slug, focus, upstream, t, now) {
  const ago = (now - t) / 60e3;
  const on = (from, to) => ago <= from && ago >= to;
  const e = { req: 1, errRate: 0, p95: 1, tokens: 1, queue: 0 };
  if (!scn || scn === 'calm') return e;
  if (scn === 'spike' && slug === focus && on(12, 0)) e.req = 8;
  if (scn === 's5xx' && slug === focus && on(8, 0)) e.errRate = 0.06;
  if (scn === 'latency' && slug === focus && on(15, 0)) e.p95 = 1 + 7 * (1 - ago / 15);
  if (scn === 'cost' && slug === focus && on(30, 0)) e.tokens = 10;
  if (scn === 'queue' && slug === focus && on(20, 0)) { e.queue = Math.round((20 - ago) * 6); e.req = 0.7; }
  if (scn === 'idle' && slug === focus && ago <= 26 * 60) e.req = 0;
  if (scn === 'service-down' && slug === focus && on(6, 0)) { e.errRate = 0.12; e.req = 0.6; }
  if (scn === 'cascade') {
    if (slug === upstream && on(9, 0)) e.errRate = 0.08;
    if (slug === focus && on(7, 0)) { e.p95 = 4; e.errRate = 0.03; }
  }
  return e;
}

export function simulate({ roster = [], scenario = 'calm', seed = 7, now = Date.now(), windowMs = 86400e3, focusSlug = null } = {}) {
  const nodes = roster.filter((n) => n.slug);
  const managed = nodes.filter((n) => !n.pool && n.envRole !== 'uat' && !n.external);
  const focus = focusSlug || managed[0]?.slug || nodes[0]?.slug || null;
  const upstream = managed.find((n) => n.slug !== focus)?.slug || null;
  const stepMs = 60e3;
  const n = Math.round(windowMs / stepMs) + 1;
  const start = now - (n - 1) * stepMs;
  const P = {}; for (const x of nodes) P[x.slug] = personality(x, seed);

  // Flow graph over the roster: prod → twin, every managed/external app →
  // control plane (planeCalls), and a few app → app pairs chosen
  // deterministically (upstream → focus always, so the cascade has an edge).
  const pairs = [];
  if (upstream && focus) pairs.push([upstream, focus, 'http']);
  const r = rng(seed);
  for (let i = 0; i < nodes.length; i += 1) {
    const a = nodes[i]; if (a.pool) continue;
    if (a.twinSlug) pairs.push([a.slug, a.twinSlug, 'sync']);
    if (r() < 0.35) { const b = nodes[Math.floor(r() * nodes.length)]; if (b && b.slug !== a.slug && !b.pool) pairs.push([a.slug, b.slug, 'http']); }
  }

  const metrics = {}; const planeCalls = {}; const usageDays = {}; const queue = {};
  for (const x of nodes) {
    const p = P[x.slug]; const rows = []; const pc = [];
    const sSeed = seed ^ hash(x.slug);
    for (let i = 0; i < n; i += 1) {
      const t = start + i * stepMs;
      const f = fault(scenario, x.slug, focus, upstream, t, now);
      const noise = 0.7 + 0.6 * noiseAt(sSeed, i / 7);
      const req = Math.max(0, Math.round(p.base * diurnal(t) * noise * f.req));
      const errRate = Math.min(1, p.errFloor + f.errRate);
      const s5xx = Math.round(req * errRate * (0.6 + 0.8 * noiseAt(sSeed ^ 1, i / 3)));
      const s4xx = Math.round(req * 0.02 * noiseAt(sSeed ^ 2, i / 5));
      const p95 = req ? Math.round(p.p95 * (0.8 + 0.4 * noiseAt(sSeed ^ 3, i / 9)) * f.p95) : 0;
      const tokens = Math.round(req * p.tokensPerReq * 0.15 * f.tokens * (0.5 + noiseAt(sSeed ^ 4, i / 11)));
      const convos = req && i % p.convoEvery === 0 ? 1 + Math.round(noiseAt(sSeed ^ 5, i) * 2) : 0;
      const src = {};
      for (const [from, to] of pairs) if (to === x.slug && req) src[from] = Math.max(1, Math.round(req * 0.08 * (0.5 + noiseAt(sSeed ^ hash(from), i / 4))));
      // Route mix for the doors (keys use `_` for `/`, as the edge rollups do).
      const routes = req ? { _: Math.round(req * 0.42), _rest_items: Math.round(req * 0.22), _graphql: Math.round(req * 0.11), _auth_v1_token: Math.round(req * 0.07), _mcp: Math.round(req * 0.05), _storage_v1_object: Math.round(req * 0.06), _agent_chat: Math.round(req * 0.04), _realtime_v1: Math.round(req * 0.03) } : {};
      rows.push({ ts: t, req, s4xx, s5xx, bytes: Math.round(req * p.bytesPerReq), p50: Math.round(p95 * 0.45), p95, uniqIps: Math.round(req * 0.3), tokens, convos, src, routes });
      if (p.kind !== 'pool' && i % 5 === 0) pc.push({ ts: t, n: 1 + (i % 15 === 0 ? 1 : 0) });
    }
    metrics[x.slug] = rows; planeCalls[x.slug] = pc;
    const q = fault(scenario, x.slug, focus, upstream, now, now).queue;
    const running = q ? 0 : 1 + Math.round(noiseAt(sSeed ^ 6, now / 6e5));
    const stale = q > 30 ? 1 : 0;
    // The queue scenario fills every lane the kit reports: fan-out jobs
    // back up behind the stale claim; dev-console deploys and the knowledge
    // index wait behind them.
    const lanes = {
      jobs: { label: 'fan-out jobs', queued: q, running, stale, oldestQueuedMs: q ? Math.round((q / 6) * 60e3) : 0 },
      devJobs: { label: 'dev console deploys', queued: q ? 2 : 0, running: q ? 0 : Math.round(noiseAt(sSeed ^ 7, now / 9e5)), stale: 0, oldestQueuedMs: q ? 9 * 60e3 : 0 },
      knowledge: { label: 'knowledge index', queued: q ? 5 : 0, running: 0, stale: 0, oldestQueuedMs: q ? 41 * 60e3 : 0 },
    };
    const sum = (k) => Object.values(lanes).reduce((a, l) => a + l[k], 0);
    queue[x.slug] = {
      at: now, totals: { queued: sum('queued'), running: sum('running'), stale: sum('stale') },
      queues: lanes,
      exceptions: stale ? [{ at: now - 4 * 60e3, where: 'fanout', message: 'runner heartbeat missed — task heartbeat timeout' }] : [],
      exceptionsLastHour: stale ? 3 : 0, activeUsersToday: Math.round(p.base * 1.8), activeUsers30m: Math.round(p.base * 0.3 * diurnal(now)),
    };
    // Daily cumulative token snapshots for the usage skyline (30 days).
    const days = {};
    for (let d = 29; d >= 0; d -= 1) {
      const dayTs = now - d * 86400e3; const key = new Date(dayTs).toISOString().slice(0, 10);
      const quiet = scenario === 'idle' && x.slug === focus && d <= 1;
      days[key] = { tokens: quiet ? 0 : Math.round(p.base * 1440 * 0.6 * p.tokensPerReq * 0.15 * (0.6 + 0.8 * noiseAt(sSeed ^ 8, d))), aiTurns: quiet ? 0 : Math.round(p.base * 40 * (0.5 + noiseAt(sSeed ^ 9, d))) };
    }
    usageDays[x.slug] = days;
  }
  const flows = pairs.map(([from, to, kind]) => ({ from, to, kind, n: 0 }));
  return { source: 'sim', scenario, focus, upstream, metrics, planeCalls, flows, usageDays, runtime: queue, note: SCENARIOS.find((s) => s.id === scenario)?.text || '' };
}

// Virtual clock: advances `speed`× real time from the moment the sim starts.
export function simClock(startedAt, speed = 1, realNow = Date.now()) {
  return startedAt + (realNow - startedAt) * speed;
}
